import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { post } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import Page from "@/components/Page";
import Confirm from "@/components/Confirm";
import ConnectedAppsCard from "@/features/mcp/ConnectedAppsCard";
import CopyButton from "@/components/CopyButton";
import { useToast } from "@/components/Toast";
import MfaEnrolment from "@/portal/components/MfaEnrolment";
import { MONO } from "@/portal/helpers";

/**
 * Your own account (/profile): two-factor sign-in and your password.
 *
 * Partners and sales staff sign in from anywhere, so this is where they turn
 * on the second factor — and where key personnel are sent when the keystore
 * refuses them for not having it.
 */
export default function AccountPage() {
  const { me, refresh, can } = useAuth();
  const toast = useToast();
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [turningOff, setTurningOff] = useState(false);
  const [offPassword, setOffPassword] = useState("");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const begin = useMutation({
    mutationFn: () => post<{ secret: string; provisioning_uri: string }>("/auth/mfa/setup"),
    onSuccess: (result) => setSetup({ secret: result.secret, otpauth_uri: result.provisioning_uri }),
    onError: (error) => toast.failed(error),
  });
  const activate = useMutation({
    mutationFn: (code: string) => post<{ recovery_codes: string[] }>("/auth/mfa/activate", { code }),
    onSuccess: async (result) => {
      setSetup(null);
      setRecovery(result.recovery_codes);
      await refresh();
      toast.notify("Two-factor sign-in is on");
    },
  });
  const disable = useMutation({
    mutationFn: () => post("/auth/mfa/disable", { password: offPassword }),
    onSuccess: async () => {
      setTurningOff(false);
      setOffPassword("");
      await refresh();
      toast.notify("Two-factor sign-in is off");
    },
  });
  const changePassword = useMutation({
    mutationFn: () => {
      if (next !== confirm) throw new Error("The two new passwords do not match");
      return post("/auth/password", { current_password: current, new_password: next });
    },
    onSuccess: () => {
      setCurrent(""); setNext(""); setConfirm("");
      toast.notify("Password changed — other sessions were signed out");
    },
  });

  if (!me) return null;
  const mfaOn = !!me.user.totp?.enabled;
  const keyPersonnel = me.user.role_ids.includes("key_custodian");

  return (
    <Page title="Your account" subtitle={`${me.user.email} · ${me.org.name}`}>
      <Stack spacing={3} sx={{ maxWidth: 640 }}>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Two-factor sign-in</Typography>
              <Typography variant="body2" color="text.secondary">
                A code from an authenticator app at every sign-in, so a leaked password alone
                does not open your account.
              </Typography>
            </Box>
            <Chip size="small" label={mfaOn ? "On" : "Off"} color={mfaOn ? "success" : "default"}
                  variant={mfaOn ? "filled" : "outlined"} />
          </Stack>

          {recovery ? (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRecovery(null)}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Keep these recovery codes somewhere safe. Each one signs you in once if you lose
                your phone. They are not shown again.
              </Typography>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box component="pre" sx={{ fontFamily: MONO, fontSize: 13, m: 0, flex: 1 }}>
                  {recovery.join("\n")}
                </Box>
                <CopyButton value={recovery.join("\n")} label="Copy codes" />
              </Stack>
            </Alert>
          ) : null}

          {mfaOn ? (
            keyPersonnel ? (
              <Typography variant="body2" color="text.secondary">
                Key personnel must keep two-factor sign-in on.
              </Typography>
            ) : (
              <Button variant="outlined" color="error" onClick={() => setTurningOff(true)}
                      disabled={me.org.kind === "vendor" && !!me.staff_mfa_required_from
                        && new Date() >= new Date(me.staff_mfa_required_from)}>
                Turn off
              </Button>
            )
          ) : setup ? (
            <Box>
              <MfaEnrolment setup={setup} busy={activate.isPending} error={activate.error}
                            onActivate={(code) => activate.mutate(code)} />
              <Button sx={{ mt: 1 }} onClick={() => { setSetup(null); activate.reset(); }}>
                Cancel
              </Button>
            </Box>
          ) : (
            <Button variant="contained" onClick={() => begin.mutate()} disabled={begin.isPending}>
              {begin.isPending ? "Preparing…" : "Set up two-factor"}
            </Button>
          )}
        </Paper>

        {me.org.kind === "vendor" && can("assistant.operate") ? <ConnectedAppsCard /> : null}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>Password</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Changing it signs you out everywhere else.
          </Typography>
          <Stack component="form" spacing={1.5}
                 onSubmit={(event) => { event.preventDefault(); changePassword.mutate(); }}>
            <TextField size="small" type="password" label="Current password" value={current}
                       autoComplete="current-password"
                       onChange={(event) => setCurrent(event.target.value)} />
            <TextField size="small" type="password" label="New password" value={next}
                       autoComplete="new-password"
                       onChange={(event) => setNext(event.target.value)} />
            <TextField size="small" type="password" label="New password again" value={confirm}
                       autoComplete="new-password"
                       onChange={(event) => setConfirm(event.target.value)} />
            {changePassword.error ? (
              <Alert severity="error">{(changePassword.error as Error).message}</Alert>
            ) : null}
            <Box>
              <Button type="submit" variant="contained"
                      disabled={!current || !next || changePassword.isPending}>
                {changePassword.isPending ? "Saving…" : "Change password"}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <Confirm
        open={turningOff}
        title="Turn off two-factor sign-in?"
        confirmLabel="Turn off"
        danger
        busy={disable.isPending}
        confirmDisabled={!offPassword}
        error={disable.error}
        onCancel={() => { setTurningOff(false); setOffPassword(""); disable.reset(); }}
        onConfirm={() => disable.mutate()}
        body={
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2">You will sign in with your password alone.</Typography>
            <TextField autoFocus size="small" type="password" label="Your password"
                       value={offPassword} onChange={(event) => setOffPassword(event.target.value)} />
          </Stack>
        }
      />
    </Page>
  );
}
